import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  LuBuilding2,
  LuCalendarRange,
  LuFileSearch,
  LuLandmark,
  LuScale,
  LuUsers,
} from "react-icons/lu";

import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { SelectButton } from "primereact/selectbutton";
import { Toast } from "primereact/toast";

import api from "../../../services/api";
import { filtroDeHorasPendentes } from "../../../services/responsavelLogado";

import {
  DateFilter,
  DateFilterValue,
  Field,
  FilterActions,
  FilterStyles,
  Section,
} from "../../../components/FilterKit";

type Id = string | number;
interface Option {
  name: string;
  value: Id;
}

interface Filters {
  proc_reg: string;
  proc_num_pasta: string;
  proc_reg_outros: string;
  proc_cod_acao: Id;
  proc_cod_natureza: Id;
  proc_orgao_cidade: string;
  proc_orgao_uf: Id;
  proc_autor: string;
  proc_reu: string;
  proc_adv_adversa: string;
  proc_cod_cliente: Id;
  proc_cod_adv_resp: Id;
  proc_cod_ass_resp: Id;
  proc_em_acordo: string;
  updatedin: number;
  nochanges: number;
  ajuizamento: DateFilterValue;
  ingresso: DateFilterValue;
  desfecho: DateFilterValue;
}

const emptyFilters = (): Filters => ({
  proc_reg: "",
  proc_num_pasta: "",
  proc_reg_outros: "",
  proc_cod_acao: "",
  proc_cod_natureza: "",
  proc_orgao_cidade: "",
  proc_orgao_uf: "",
  proc_autor: "",
  proc_reu: "",
  proc_adv_adversa: "",
  proc_cod_cliente: "0",
  proc_cod_adv_resp: "",
  proc_cod_ass_resp: "",
  proc_em_acordo: "3",
  updatedin: 0,
  nochanges: 0,
  ajuizamento: { mode: "data", start: null, end: null },
  ingresso: { mode: "data", start: null, end: null },
  desfecho: { mode: "data", start: null, end: null },
});

const statusOptions = [
  { name: "Ativos", value: "0" },
  { name: "Em acordo", value: "1" },
  { name: "Arquivados", value: "2" },
  { name: "Ativos + em acordo", value: "3" },
  { name: "Todos", value: "4" },
];

const brazilianStates: Option[] = [
  "AC", "AL", "AM", "AP", "BA", "CE", "DF", "ES", "GO", "MA", "MG", "MS", "MT", "PA",
  "PB", "PE", "PI", "PR", "RJ", "RN", "RO", "RR", "RS", "SC", "SE", "SP", "TO",
].map((uf) => ({ name: uf, value: uf }));

const loadOptions = async (
  url: string,
  idField: string,
  nameField: string
): Promise<Option[]> => {
  const { data } = await api.get(url);
  return data.map((item: Record<string, string | number>) => ({
    name: String(item[nameField]),
    value: item[idField],
  }));
};

const useOptions = (url: string, idField: string, nameField: string) => {
  const [options, setOptions] = useState<Option[]>([]);

  useEffect(() => {
    let alive = true;
    loadOptions(url, idField, nameField)
      .then((loaded) => alive && setOptions(loaded))
      .catch(() => alive && setOptions([]));
    return () => {
      alive = false;
    };
  }, [url, idField, nameField]);

  return options;
};

const countActive = (filters: Filters): number => {
  const initial = emptyFilters();
  const text = [
    "proc_reg",
    "proc_num_pasta",
    "proc_reg_outros",
    "proc_orgao_cidade",
    "proc_autor",
    "proc_reu",
    "proc_adv_adversa",
  ] as const;
  const choice = [
    "proc_cod_acao",
    "proc_cod_natureza",
    "proc_orgao_uf",
    "proc_cod_cliente",
    "proc_cod_adv_resp",
    "proc_cod_ass_resp",
  ] as const;

  let total = 0;
  text.forEach((key) => {
    if (filters[key].trim() !== "") total += 1;
  });
  choice.forEach((key) => {
    if (filters[key] !== initial[key] && filters[key] !== "") total += 1;
  });
  if (filters.proc_em_acordo !== initial.proc_em_acordo) total += 1;
  if (filters.updatedin > 0) total += 1;
  if (filters.nochanges > 0) total += 1;
  [filters.ajuizamento, filters.ingresso, filters.desfecho].forEach((range) => {
    if (range.start || range.end) total += 1;
  });
  return total;
};

const SearchLawsuit: React.FC = () => {
  const toast = useRef<Toast | null>(null);
  const history = useHistory();
  const [filters, setFilters] = useState<Filters>(emptyFilters);

  const suitTypes = useOptions("lawsuits/suittypes/show/all", "acao_id", "acao_descricao");
  const natures = useOptions("lawsuits/naturesuits/show/all", "natureza_id", "natureza_descricao");
  const attorneys = useOptions("management/attorneys/show/all", "advogado_id", "advogado_nome");
  const assistants = useOptions("management/assistants/show/all", "administrador_id", "administrador_nome");
  const customers = useOptions("customers/show/all", "cliente_id", "cliente_nome");

  const set = useCallback(
    <K extends keyof Filters>(key: K, value: Filters[K]) =>
      setFilters((current) => ({ ...current, [key]: value })),
    []
  );

  const active = useMemo(() => countActive(filters), [filters]);

  useEffect(() => {
    (async () => {
      const filtro = await filtroDeHorasPendentes();
      if (!filtro) return;

      const { data } = await api.post(
        "/management/workinghours/pendingconclusion/count",
        filtro
      );
      const total = Number(data[0].total);
      if (total > 0) {
        toast.current?.show({
          severity: "warn",
          sticky: true,
          content: (
            <div className="flex flex-column" style={{ flex: "1" }}>
              <div className="text-center">
                <i
                  className="pi pi-exclamation-triangle"
                  style={{ fontSize: "3rem" }}
                />
                <h4>
                  Você possui{" "}
                  <b>
                    {total} {total < 2 ? "hora pendente" : "horas pendentes"}
                  </b>
                  <br />
                  de finalização para concluir o cadastro.
                </h4>
                <p>
                  Deseja finalizar{" "}
                  {total < 2 ? " esta hora pendente" : " estas horas pendentes"}
                  ?
                  <br />
                  <br />
                </p>
              </div>
              <div className="grid p-fluid">
                <div className="col-6">
                  <Button
                    type="button"
                    label="Finalizar agora"
                    className="p-button-info"
                    onClick={() =>
                      history.push("/management/workinghour/pendingconclusion")
                    }
                  />
                </div>
                <div className="col-6">
                  <Button
                    type="button"
                    label="Ainda não"
                    className="p-button-danger"
                    onClick={() => toast.current?.clear()}
                  />
                </div>
              </div>
            </div>
          ),
        });
      }
    })().catch(() => undefined);
  }, [history]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    history.push({
      pathname: "/lawsuit/main",
      state: {
        proc_reg: filters.proc_reg,
        proc_num_pasta: filters.proc_num_pasta,
        proc_reg_outros: filters.proc_reg_outros,
        proc_cod_acao: filters.proc_cod_acao,
        proc_cod_natureza: filters.proc_cod_natureza,
        proc_orgao_cidade: filters.proc_orgao_cidade,
        proc_orgao_uf: filters.proc_orgao_uf,
        proc_autor: filters.proc_autor,
        proc_reu: filters.proc_reu,
        proc_adv_adversa: filters.proc_adv_adversa,
        proc_cod_cliente: filters.proc_cod_cliente,
        proc_cod_adv_resp: filters.proc_cod_adv_resp,
        proc_cod_ass_resp: filters.proc_cod_ass_resp,
        dateFilterType: filters.ajuizamento.mode,
        registeredFilterType: filters.ingresso.mode,
        proc_data_ajuiz_inicio: filters.ajuizamento.start,
        proc_data_ajuiz_final: filters.ajuizamento.end,
        proc_data_ingresso_inicio: filters.ingresso.start,
        proc_data_ingresso_final: filters.ingresso.end,
        proc_desfecho_data_inicio: filters.desfecho.start,
        proc_desfecho_data_final: filters.desfecho.end,
        proc_em_acordo: filters.proc_em_acordo,
        updatedin: filters.updatedin,
        nochanges: filters.nochanges,
        formValuesChanged: false,
      },
    });
  };

  const textInput = (key: keyof Filters, id: string, autoFocus = false) => (
    <InputText
      id={id}
      name={id}
      value={filters[key] as string}
      onChange={(e) => set(key, e.target.value as never)}
      autoFocus={autoFocus}
    />
  );

  const dropdown = (
    key: keyof Filters,
    id: string,
    options: Option[],
    placeholder: string,
    emptyValue: Id
  ) => (
    <Dropdown
      inputId={id}
      name={id}
      options={options}
      optionLabel="name"
      optionValue="value"
      value={filters[key] === emptyValue ? null : (filters[key] as Id)}
      onChange={(e) => set(key, (e.value ?? emptyValue) as never)}
      placeholder={placeholder}
      filter
      resetFilterOnHide
      filterBy="name"
      showClear
      emptyFilterMessage="Nenhum registro encontrado"
      emptyMessage="Nenhum registro encontrado"
      appendTo={document.body}
    />
  );

  return (
    <FilterStyles>
      <Toast ref={toast} position="bottom-right" />

      <form className="card search-card" onSubmit={handleSubmit}>
        <header className="search-title">
          <h3>Consultar processos</h3>
          <span className="search-hint">
            Preencha só o que precisar. Enter pesquisa.
          </span>
        </header>

        <div className="search-grid">
          <div className="search-col">
            <Section icon={<LuFileSearch />} title="Identificação">
              <div className="row r3">
                <Field id="proc_reg" label="Nº do processo">
                  {textInput("proc_reg", "proc_reg", true)}
                </Field>
                <Field id="proc_num_pasta" label="Pasta nº">
                  {textInput("proc_num_pasta", "proc_num_pasta")}
                </Field>
                <Field id="proc_reg_outros" label="Outros nrs. do processo">
                  {textInput("proc_reg_outros", "proc_reg_outros")}
                </Field>
              </div>
            </Section>

            <Section icon={<LuScale />} title="Partes">
              <div className="row r3">
                <Field id="proc_autor" label="Autor">
                  {textInput("proc_autor", "proc_autor")}
                </Field>
                <Field id="proc_reu" label="Réu">
                  {textInput("proc_reu", "proc_reu")}
                </Field>
                <Field id="proc_adv_adversa" label="Advogado parte adversa">
                  {textInput("proc_adv_adversa", "proc_adv_adversa")}
                </Field>
              </div>
            </Section>

            <Section icon={<LuUsers />} title="Responsáveis">
              <div className="row r3">
                <Field id="proc_cliente" label="Cliente">
                  {dropdown("proc_cod_cliente", "proc_cliente", customers, "Todos", "0")}
                </Field>
                <Field id="proc_cod_adv_resp" label="Adv. responsável">
                  {dropdown("proc_cod_adv_resp", "proc_cod_adv_resp", attorneys, "Todos", "")}
                </Field>
                <Field id="proc_cod_ass_resp" label="Assistente">
                  {dropdown("proc_cod_ass_resp", "proc_cod_ass_resp", assistants, "Todos", "")}
                </Field>
              </div>
            </Section>

            <Section icon={<LuLandmark />} title="Classificação e local">
              <div className="row r4">
                <Field id="proc_cod_acao" label="Tipo de ação">
                  {dropdown("proc_cod_acao", "proc_cod_acao", suitTypes, "Todas", "")}
                </Field>
                <Field id="proc_cod_natureza" label="Natureza do processo">
                  {dropdown("proc_cod_natureza", "proc_cod_natureza", natures, "Todas", "")}
                </Field>
                <Field id="proc_orgao_cidade" label="Cidade">
                  {textInput("proc_orgao_cidade", "proc_orgao_cidade")}
                </Field>
                <Field id="proc_orgao_uf" label="Estado">
                  {dropdown("proc_orgao_uf", "proc_orgao_uf", brazilianStates, "UF", "")}
                </Field>
              </div>
            </Section>
          </div>

          <div className="search-col">
            <Section icon={<LuCalendarRange />} title="Datas">
              <DateFilter
                id="ajuizamento"
                label="Ajuizamento"
                withMode
                value={filters.ajuizamento}
                onChange={(next) => set("ajuizamento", next)}
              />
              <DateFilter
                id="ingresso"
                label="Ingresso no escritório"
                withMode
                value={filters.ingresso}
                onChange={(next) => set("ingresso", next)}
              />
              <DateFilter
                id="desfecho"
                label="Desfecho"
                withMode={false}
                value={filters.desfecho}
                onChange={(next) => set("desfecho", next)}
              />
            </Section>

            <Section icon={<LuBuilding2 />} title="Situação do processo">
              <Field id="proc_em_acordo" label="Incluir processos">
                <SelectButton
                  className="status-buttons"
                  id="proc_em_acordo"
                  value={filters.proc_em_acordo}
                  options={statusOptions}
                  optionLabel="name"
                  unselectable={false}
                  onChange={(e) => e.value && set("proc_em_acordo", e.value)}
                />
              </Field>
              <div className="idle-filters">
                <label htmlFor="updatedin">
                  Sem atualização nos últimos
                  <InputNumber
                    inputId="updatedin"
                    value={filters.updatedin}
                    onValueChange={(e) => set("updatedin", Number(e.value ?? 0))}
                    min={0}
                    useGrouping={false}
                  />
                  dias
                </label>
                <label htmlFor="nochanges">
                  Sem mov. relevante nos últimos
                  <InputNumber
                    inputId="nochanges"
                    value={filters.nochanges}
                    onValueChange={(e) => set("nochanges", Number(e.value ?? 0))}
                    min={0}
                    useGrouping={false}
                  />
                  dias
                </label>
                <small>Deixe 0 para não filtrar por esses prazos.</small>
              </div>
            </Section>
          </div>
        </div>

        <FilterActions
          active={active}
          submitLabel="Pesquisar processos"
          onClear={() => setFilters(emptyFilters())}
        />
      </form>
    </FilterStyles>
  );
};

export default SearchLawsuit;
